import { Link } from "react-router-dom";
import { useConfig, realPhone, realEmail, realAddress } from "@/context/ConfigContext";
import { useLayout, FOOTER_FALLBACK, isInternal } from "@/context/LayoutContext";
import { activeSocialLinks, providerMeta } from "@/lib/social";
import { fileUrl } from "@/lib/api";
import { Phone, Mail, MapPin } from "lucide-react";

function FooterLink({ href, children }) {
  return isInternal(href)
    ? <Link to={href} className="hover:text-saffron">{children}</Link>
    : <a href={href} target="_blank" rel="noopener noreferrer" className="hover:text-saffron">{children}</a>;
}

export function Footer() {
  const config = useConfig();
  const layout = useLayout();
  const fd = layout.footer || FOOTER_FALLBACK;
  const columns = fd.columns && fd.columns.length ? fd.columns : FOOTER_FALLBACK.columns;
  const legal = fd.legal_links && fd.legal_links.length ? fd.legal_links : FOOTER_FALLBACK.legal_links;
  const copyright = (fd.copyright || FOOTER_FALLBACK.copyright).replace("{year}", new Date().getFullYear());
  const disclosure = fd.disclosure || FOOTER_FALLBACK.disclosure;
  const logo = config.logo_url ? fileUrl(config.logo_url) : null;
  return (
    <footer data-testid="site-footer" className="bg-charcoal text-cream/80">
      <div className="paithani-border" />
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-4">
          <div className="lg:col-span-1">
            {logo ? (
              <div className="inline-block rounded-xl bg-cream p-3">
                <img src={logo} alt="Maha Sampada — Discover Maharashtra's Treasures" onError={(e) => { e.currentTarget.style.display = "none"; }}
                  style={{ width: config.logo_w_footer || 220, height: "auto" }} className="w-auto object-contain" />
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-saffron text-white font-display text-xl font-bold">M</div>
                <div className="font-display text-2xl font-bold text-cream">Maha Sampada</div>
              </div>
            )}
            <p className="mt-4 text-sm leading-relaxed text-cream/70">{fd.tagline || FOOTER_FALLBACK.tagline}</p>
            {fd.show_social !== false && activeSocialLinks(config).length > 0 && (
            <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="footer-social">
              {activeSocialLinks(config).map((l) => {
                const meta = providerMeta(l.provider);
                const Icon = meta.Icon;
                return (
                  <a key={`${l.provider}-${l.url}`} href={l.url} target="_blank" rel="noopener noreferrer nofollow"
                     data-testid={`footer-social-${l.provider}`} aria-label={meta.label}
                     className="flex h-9 w-9 items-center justify-center rounded-full border border-cream/20 transition-colors hover:bg-saffron hover:border-saffron">
                    <Icon className="h-4 w-4" />
                  </a>
                );
              })}
            </div>
            )}
          </div>

          {columns.map((col, ci) => (
            <div key={ci} data-testid={`footer-col-${ci}`}>
              <h4 className="overline text-gold">{col.title}</h4>
              <ul className="mt-4 space-y-2 text-sm">
                {(col.links || []).map((l, li) => <li key={li}><FooterLink href={l.href}>{l.label}</FooterLink></li>)}
              </ul>
            </div>
          ))}

          {fd.show_contact !== false && (
          <div>
            <h4 className="overline text-gold">Contact</h4>
            <ul className="mt-4 space-y-3 text-sm">
              {realAddress(config.address) && <li className="flex items-start gap-2"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-saffron" /><span>{config.address}</span></li>}
              {realPhone(config.phone) && <li className="flex items-center gap-2"><Phone className="h-4 w-4 text-saffron" /><span>{config.phone}</span></li>}
              {realEmail(config.email_general) && <li className="flex items-center gap-2"><Mail className="h-4 w-4 text-saffron" /><span>{config.email_general}</span></li>}
            </ul>
            {config.business_hours && <p className="mt-3 text-xs text-cream/50">Business hours: {config.business_hours}</p>}
          </div>
          )}
        </div>

        <div className="mt-12 border-t border-cream/15 pt-6">
          {disclosure && <p className="mb-4 text-xs leading-relaxed text-cream/50" data-testid="footer-disclosure">{disclosure}</p>}
          <div className="flex flex-col gap-4 text-xs text-cream/60 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-wrap gap-4">
              {legal.map((l, i) => <FooterLink key={i} href={l.href}>{l.label}</FooterLink>)}
              <Link to="/service-provider-registration" className="hover:text-saffron" data-testid="footer-service-provider-link">Register as a Service Provider</Link>
            </div>
            <div>{copyright}</div>
          </div>
        </div>
      </div>
    </footer>
  );
}
